import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from "chart.js";
import { Bar } from "react-chartjs-2";
import { Download, Target, AlertTriangle, CheckCircle, XCircle, Clock, Shield, Upload, Image, X, Building, Mail, Globe, FileText, Save, Eye } from "lucide-react";
import { apiRequest } from "../utils/api";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Styled Components
const ReportWrapper = styled.div`
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: #1f2937;
  background: #f8fafc;
  min-height: 100vh;
`;

const PreviewContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
`;

const PreviewCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

const PreviewHeader = styled.div`
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  color: white;
  padding: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PreviewTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  text-decoration: none;
  opacity: ${props => props.disabled ? 0.7 : 1};

  &:hover {
    background: rgba(255, 255, 255, ${props => props.disabled ? 0.15 : 0.25});
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    color: white;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  gap: 1rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #1562b7;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #64748b;
  text-align: center;
`;

const ChartSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
`;

const ChartTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 1rem;
  text-align: center;
`;

const ChartContainer = styled.div`
  height: 280px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MetricsCard = styled.div`
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SummaryStats = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin: 0 2rem 2rem 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: ${props => props.color || '#f8fafc'};
  color: white;
  padding: 1rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.9;
`;

const ScanInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin: 2rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InfoValue = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
`;

const VulnList = styled.div`
  margin: 2rem;
`;

const VulnItem = styled.div`
  background: ${props => props.severity === 'Critical' ? '#fff1f0' : 
    props.severity === 'High' ? '#fff4ed' : 
    props.severity === 'Medium' ? '#fffbeb' : 
    props.severity === 'Low' ? '#f0fdf4' : '#eff6ff'};
  border-left: 4px solid ${props => props.color};
  padding: 1rem;
  margin-bottom: 0.75rem;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const VulnName = styled.div`
  font-weight: 600;
  color: #1e293b;
  flex: 1;
`;

const SeverityBadge = styled.span`
  background: ${props => props.color};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const PocIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #2563eb;
  margin-left: 0.5rem;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  padding: 1rem;
  border-radius: 8px;
  margin: 2rem;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: #dcfce7;
  color: #166534;
  padding: 1rem;
  border-radius: 8px;
  margin: 2rem;
  text-align: center;
`;

// White-label Form Styled Components
const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  margin: 2rem 2rem 0 2rem;
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.active ? '#1562b7' : 'transparent'};
  color: ${props => props.active ? 'white' : '#64748b'};
  border: none;
  border-radius: 8px 8px 0 0;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#1562b7' : '#f1f5f9'};
    color: ${props => props.active ? 'white' : '#1e293b'};
  }
`;

const FormContainer = styled.div`
  margin: 2rem;
  padding: 2rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const FormTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const FormInput = styled.input`
  padding: 0.75rem;
  border: 1px solid ${props => props.error ? '#ef4444' : '#e2e8f0'};
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #1562b7;
    box-shadow: 0 0 0 3px rgba(21, 98, 183, 0.1);
  }

  &:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
  }
`;

const FormTextarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #1562b7;
    box-shadow: 0 0 0 3px rgba(21, 98, 183, 0.1);
  }
`;

const LogoUploadArea = styled.div`
  border: 2px dashed ${props => props.error ? '#ef4444' : '#cbd5e1'};
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.hasLogo ? '#f0f9ff' : 'white'};

  &:hover {
    border-color: #1562b7;
    background: #f8fafc;
  }
`;

const LogoPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const LogoImage = styled.img`
  max-width: 200px;
  max-height: 80px;
  object-fit: contain;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.5rem;
  background: white;
`;

const LogoPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
`;

const LogoInstructions = styled.p`
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.5rem;
`;

const HiddenInput = styled.input`
  display: none;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #1562b7;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover {
    background: #1e4b8a;
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  color: #64748b;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    color: #1e293b;
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: #fee2e2;
  color: #991b1b;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: none;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fecaca;
  }
`;

const FormError = styled.span`
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const InfoMessage = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  padding: 1rem;
  border-radius: 8px;
  margin: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
`;

// Helper functions
const getSeverityColor = (severity) => {
  const colors = {
    'Critical': '#7f1d1d',
    'High': '#dc2626',
    'Medium': '#f59e0b',
    'Low': '#16a34a',
    'Informational': '#2563eb'
  };
  return colors[severity] || '#64748b';
};

const getSeverityBgColor = (severity) => {
  const colors = {
    'Critical': '#7f1d1d',
    'High': '#b91c1c',
    'Medium': '#d97706',
    'Low': '#059669',
    'Informational': '#1d4ed8'
  };
  return colors[severity] || '#4b5563';
};

// Convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

function ReportPdfPage() {
  const { scanType, scanId } = useParams();
  const navigate = useNavigate();
  
  // State for report data
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for UI
  const [activeTab, setActiveTab] = useState('preview');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  
  // State for whitelabel form
  const [whitelabelData, setWhitelabelData] = useState({
    brand_name: '',
    brand_tagline: '',
    company_name: '',
    contact_email: '',
    website: '',
    footer_text: '',
    logo_base64: null
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [whitelabelEnabled, setWhitelabelEnabled] = useState(true);

  // Fetch report data for preview
  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // This endpoint should return JSON data for preview
        const resp = await apiRequest(`/api/report/${scanType}/${scanId}`, { method: "GET" });
        
        if (!resp.ok) {
          throw new Error(`Failed to fetch report: ${resp.statusText}`);
        }
        
        const data = await resp.json();

        let transformed = null;
        if (data.scanData && data.scanData.length > 0) {
          const scan = data.scanData[0];
          let vulnerabilities = [];

          if (data.vulnerabilityScanData && data.vulnerabilityScanData.length > 0) {
            (data.vulnerabilityScanData || []).forEach((scanItem) => {
              Object.values(scanItem.results || {}).forEach((vuln) => {
                if (vuln && vuln.isVulnerable) {
                  vulnerabilities.push({
                    name: vuln.name,
                    severity: vuln.severity,
                    description: vuln.description,
                    poc: vuln.poc,
                    category: vuln.owaspCategory || vuln.owaspCategory,
                    details: vuln.details,
                    remediation: vuln.remediation ? (Array.isArray(vuln.remediation) ? vuln.remediation : [vuln.remediation]) : [],
                    codeRemediation: vuln.codeRemediation || vuln.codeRemidiation || "",
                  });
                }
              });
            });
          }

          transformed = {
            scanId: scan.scanId,
            status: scan.status,
            targetUrl: scan.url,
            progress: scan.progress,
            asset: scan.asset?.name || scan.asset?.url || scan.url,
            assetType: scan.asset?.assetType || "Web Application",
            clientName: scan.name || '',
            clientEmail: scan.email || '',
            clientOrganization: scan.organization || '',
            vulnerabilityCounts: {
              critical: scan.scanResult?.Critical || 0,
              high: scan.scanResult?.High || 0,
              medium: scan.scanResult?.Medium || 0,
              low: scan.scanResult?.Low || 0,
              informational: scan.scanResult?.Info || 0,
            },
            vulnerabilities,
            totalVulnerabilities: vulnerabilities.length,
            startTime: scan.startTime,
            endTime: scan.endTime,
            version: scan.version,
          };
          
          // Pre-fill whitelabel form with some defaults
          setWhitelabelData(prev => ({
            ...prev,
            company_name: scan.organization || 'Your Company',
            contact_email: scan.email || '',
            brand_name: scan.organization || 'Zeroday Ops',
          }));
        }
        setReportData(transformed);
      } catch (err) {
        console.error("Error fetching report:", err);
        setError(err.message || "Failed to load report data");
        setReportData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [scanType, scanId]);

  // Handle logo upload
  const handleLogoUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setFormErrors(prev => ({ ...prev, logo: 'Please upload an image file' }));
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setFormErrors(prev => ({ ...prev, logo: 'Logo must be less than 2MB' }));
      return;
    }

    try {
      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      setLogoFile(file);
      
      // Convert to base64
      const base64 = await fileToBase64(file);
      
      setWhitelabelData(prev => ({
        ...prev,
        logo_base64: base64
      }));
      
      // Clear any previous logo errors
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.logo;
        return newErrors;
      });
    } catch (error) {
      console.error('Error processing logo:', error);
      setFormErrors(prev => ({ ...prev, logo: 'Failed to process logo' }));
    }
  }, []);

  // Remove logo
  const handleRemoveLogo = useCallback(() => {
    setLogoFile(null);
    setLogoPreview(null);
    setWhitelabelData(prev => ({
      ...prev,
      logo_base64: null
    }));
  }, []);

  // Handle form input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setWhitelabelData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if exists
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [formErrors]);

  // Validate whitelabel form
  const validateForm = useCallback(() => {
    const errors = {};
    
    if (!whitelabelData.brand_name?.trim()) {
      errors.brand_name = 'Brand name is required';
    }
    
    if (!whitelabelData.company_name?.trim()) {
      errors.company_name = 'Company name is required';
    }
    
    if (whitelabelData.contact_email && !/\S+@\S+\.\S+/.test(whitelabelData.contact_email)) {
      errors.contact_email = 'Please enter a valid email';
    }
    
    if (whitelabelData.website && !whitelabelData.website.match(/^https?:\/\/.+/)) {
      errors.website = 'Please enter a valid URL starting with http:// or https://';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [whitelabelData]);

  // Download standard report (non-whitelabel) - FIXED ENDPOINT
  const handleDownloadStandardPDF = useCallback(async () => {
    if (!reportData || isDownloading) return;
    
    setIsDownloading(true);
    setDownloadSuccess(false);
    setError(null);
    
    try {
      // FIXED: Correct endpoint with /pdf/ suffix
      const response = await apiRequest(
        `/api/report/${scanType}/${scanId}/pdf/`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to generate PDF: ${response.statusText}`);
      }

      // Get the PDF blob from the response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `VAPT-Report-${reportData.scanId}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      setDownloadSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => setDownloadSuccess(false), 3000);
      
    } catch (error) {
      console.error('PDF download failed:', error);
      setError(error.message || 'Failed to download PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [reportData, scanType, scanId, isDownloading]);

  // Download branded PDF from backend - FIXED ENDPOINT
  const handleDownloadBrandedPDF = useCallback(async () => {
    if (!reportData || isDownloading) return;
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsDownloading(true);
    setDownloadSuccess(false);
    setError(null);
    
    try {
      // Prepare data for backend
      const brandingData = {
        ...whitelabelData,
        year: new Date().getFullYear()
      };
      
      // FIXED: Correct endpoint with /pdf/ suffix
      const response = await apiRequest(
        `/api/report/whitelabel/${scanType}/${scanId}/`,
        { 
          method: "POST",
          body: JSON.stringify(brandingData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to generate PDF: ${response.statusText}`);
      }

      // Get the PDF blob from the response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${whitelabelData.brand_name || 'VAPT'}-Report-${reportData.scanId}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      setDownloadSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => setDownloadSuccess(false), 3000);
      
    } catch (error) {
      console.error('PDF download failed:', error);
      setError(error.message || 'Failed to download PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [reportData, scanType, scanId, whitelabelData, isDownloading, validateForm]);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  if (loading) {
    return (
      <ReportWrapper>
        <PreviewContainer>
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Loading report data...</LoadingText>
          </LoadingContainer>
        </PreviewContainer>
      </ReportWrapper>
    );
  }

  if (error && !reportData) {
    return (
      <ReportWrapper>
        <PreviewContainer>
          <PreviewCard>
            <PreviewHeader>
              <PreviewTitle>
                <AlertTriangle size={20} />
                Error Loading Report
              </PreviewTitle>
            </PreviewHeader>
            <ErrorMessage>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Failed to load report</div>
              <div>{error}</div>
            </ErrorMessage>
          </PreviewCard>
        </PreviewContainer>
      </ReportWrapper>
    );
  }

  if (!reportData) {
    return (
      <ReportWrapper>
        <PreviewContainer>
          <PreviewCard>
            <PreviewHeader>
              <PreviewTitle>
                <AlertTriangle size={20} />
                No Data Available
              </PreviewTitle>
            </PreviewHeader>
            <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>
              <div style={{ fontSize: '1rem', fontWeight: '500' }}>No report data available for this scan.</div>
            </div>
          </PreviewCard>
        </PreviewContainer>
      </ReportWrapper>
    );
  }

  // Prepare bar chart data
  const barData = {
    labels: ["Critical", "High", "Medium", "Low", "Informational"],
    datasets: [
      {
        label: "Vulnerabilities",
        data: [
          reportData.vulnerabilityCounts.critical,
          reportData.vulnerabilityCounts.high,
          reportData.vulnerabilityCounts.medium,
          reportData.vulnerabilityCounts.low,
          reportData.vulnerabilityCounts.informational,
        ],
        backgroundColor: ["#7f1d1d", "#dc2626", "#f59e0b", "#16a34a", "#2563eb"],
        borderWidth: 0,
        borderRadius: 6,
      },
    ],
  };

  const barOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1, font: { size: 10 } },
        grid: { color: '#f1f5f9' },
      },
      x: {
        ticks: { font: { size: 10, weight: '500' } },
        grid: { display: false },
      },
    },
  };

  const pocCount = reportData.vulnerabilities.filter(vuln => vuln.poc).length;

  return (
    <ReportWrapper>
      <PreviewContainer>
        <PreviewCard>
          <PreviewHeader>
            <PreviewTitle>
              <Target size={20} />
              VAPT Report
              {pocCount > 0 && (
                <div style={{ fontSize: '0.75rem', fontWeight: '400', marginLeft: '1rem', opacity: '0.9' }}>
                  {pocCount} vulnerabilities with POC
                </div>
              )}
            </PreviewTitle>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <DownloadButton
                onClick={handleDownloadStandardPDF}
                disabled={isDownloading}
                title="Download standard report"
              >
                <FileText size={16} />
                {isDownloading ? 'Downloading...' : 'Standard Report'}
              </DownloadButton>
            </div>
          </PreviewHeader>

          {/* Tabs */}
          <TabContainer>
            <Tab 
              active={activeTab === 'preview'} 
              onClick={() => setActiveTab('preview')}
            >
              <Eye size={16} />
              Report Preview
            </Tab>
            <Tab 
              active={activeTab === 'whitelabel'} 
              onClick={() => setActiveTab('whitelabel')}
              disabled={!whitelabelEnabled}
            >
              <Building size={16} />
              White-label Branding
            </Tab>
          </TabContainer>

          {/* Messages */}
          {downloadSuccess && (
            <SuccessMessage>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                <CheckCircle size={20} />
                <span>PDF downloaded successfully!</span>
              </div>
            </SuccessMessage>
          )}

          {error && (
            <ErrorMessage>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                <XCircle size={20} />
                <span>{error}</span>
              </div>
            </ErrorMessage>
          )}

          {!whitelabelEnabled && activeTab === 'whitelabel' && (
            <InfoMessage>
              <Shield size={20} />
              White-labeling is not enabled for your organization. Please contact support to enable this feature.
            </InfoMessage>
          )}

          {/* Preview Tab Content */}
          {activeTab === 'preview' && (
            <>
              {/* Scan Information */}
              <ScanInfo>
                <InfoItem>
                  <InfoLabel>Target Asset</InfoLabel>
                  <InfoValue>{reportData.asset}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Asset Type</InfoLabel>
                  <InfoValue>{reportData.assetType}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Scan ID</InfoLabel>
                  <InfoValue>{reportData.scanId}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Assessment Date</InfoLabel>
                  <InfoValue>
                    {reportData.startTime ? new Date(reportData.startTime).toLocaleDateString() : 'N/A'}
                  </InfoValue>
                </InfoItem>
                {reportData.clientName && (
                  <InfoItem>
                    <InfoLabel>Client</InfoLabel>
                    <InfoValue>{reportData.clientName}</InfoValue>
                  </InfoItem>
                )}
                {reportData.clientOrganization && (
                  <InfoItem>
                    <InfoLabel>Organization</InfoLabel>
                    <InfoValue>{reportData.clientOrganization}</InfoValue>
                  </InfoItem>
                )}
              </ScanInfo>

              {/* Severity Stats */}
              <SummaryStats>
                <StatCard color={getSeverityBgColor('Critical')}>
                  <StatValue>{reportData.vulnerabilityCounts.critical}</StatValue>
                  <StatLabel>Critical</StatLabel>
                </StatCard>
                <StatCard color={getSeverityBgColor('High')}>
                  <StatValue>{reportData.vulnerabilityCounts.high}</StatValue>
                  <StatLabel>High</StatLabel>
                </StatCard>
                <StatCard color={getSeverityBgColor('Medium')}>
                  <StatValue>{reportData.vulnerabilityCounts.medium}</StatValue>
                  <StatLabel>Medium</StatLabel>
                </StatCard>
                <StatCard color={getSeverityBgColor('Low')}>
                  <StatValue>{reportData.vulnerabilityCounts.low}</StatValue>
                  <StatLabel>Low</StatLabel>
                </StatCard>
                <StatCard color={getSeverityBgColor('Informational')}>
                  <StatValue>{reportData.vulnerabilityCounts.informational}</StatValue>
                  <StatLabel>Info</StatLabel>
                </StatCard>
              </SummaryStats>

              {/* Charts */}
              <ChartSection>
                <ChartCard>
                  <ChartTitle>Vulnerability Severity Distribution</ChartTitle>
                  <ChartContainer>
                    <div style={{ height: '100%', width: '100%' }}>
                      <Bar data={barData} options={barOptions} />
                    </div>
                  </ChartContainer>
                </ChartCard>

                <MetricsCard>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#334155', marginBottom: '1rem', textAlign: 'center' }}>
                    Risk Metrics
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Total Vulnerabilities:</span>
                      <strong>{reportData.totalVulnerabilities}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>High & Critical:</span>
                      <strong style={{ color: '#dc2626' }}>
                        {reportData.vulnerabilityCounts.critical + reportData.vulnerabilityCounts.high}
                      </strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Risk Score:</span>
                      <strong style={{
                        color: reportData.vulnerabilityCounts.critical > 0 ? '#7f1d1d' :
                          reportData.vulnerabilityCounts.high > 0 ? '#dc2626' : '#f59e0b'
                      }}>
                        {Math.min(100, (reportData.vulnerabilityCounts.critical * 25) +
                          (reportData.vulnerabilityCounts.high * 15) +
                          (reportData.vulnerabilityCounts.medium * 8) +
                          (reportData.vulnerabilityCounts.low * 3) +
                          (reportData.vulnerabilityCounts.informational * 1))}/100
                      </strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Remediation Priority:</span>
                      <strong style={{
                        color: reportData.vulnerabilityCounts.critical > 0 ? '#7f1d1d' :
                          reportData.vulnerabilityCounts.high > 0 ? '#dc2626' : '#f59e0b'
                      }}>
                        {reportData.vulnerabilityCounts.critical > 0 ? 'IMMEDIATE' :
                          reportData.vulnerabilityCounts.high > 0 ? 'URGENT' : 'STANDARD'}
                      </strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Status:</span>
                      <strong style={{ color: reportData.status === 'completed' ? '#16a34a' : '#f59e0b' }}>
                        {reportData.status}
                      </strong>
                    </div>
                  </div>
                </MetricsCard>
              </ChartSection>

              {/* Vulnerabilities List */}
              {reportData.vulnerabilities.length > 0 && (
                <VulnList>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#334155', marginBottom: '1rem' }}>
                    Vulnerabilities Found ({reportData.totalVulnerabilities})
                  </h3>
                  {reportData.vulnerabilities.map((vuln, index) => (
                    <VulnItem 
                      key={index} 
                      severity={vuln.severity}
                      color={getSeverityColor(vuln.severity)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <VulnName>
                          {index + 1}. {vuln.name}
                          {vuln.poc && (
                            <PocIndicator>
                              <Shield size={12} />
                              POC
                            </PocIndicator>
                          )}
                        </VulnName>
                        <SeverityBadge color={getSeverityColor(vuln.severity)}>
                          {vuln.severity}
                        </SeverityBadge>
                      </div>
                    </VulnItem>
                  ))}
                </VulnList>
              )}
            </>
          )}

          {/* White-label Tab Content */}
          {activeTab === 'whitelabel' && whitelabelEnabled && (
            <FormContainer>
              <FormTitle>
                <Building size={20} />
                White-label Branding Configuration
              </FormTitle>
              
              <FormGrid>
                {/* Brand Name */}
                <FormGroup>
                  <FormLabel>
                    <Building size={14} />
                    Brand Name *
                  </FormLabel>
                  <FormInput
                    type="text"
                    name="brand_name"
                    value={whitelabelData.brand_name}
                    onChange={handleInputChange}
                    placeholder="e.g., Acme Security"
                    error={formErrors.brand_name}
                  />
                  {formErrors.brand_name && <FormError>{formErrors.brand_name}</FormError>}
                </FormGroup>

                {/* Company Name */}
                <FormGroup>
                  <FormLabel>
                    <Building size={14} />
                    Company Name *
                  </FormLabel>
                  <FormInput
                    type="text"
                    name="company_name"
                    value={whitelabelData.company_name}
                    onChange={handleInputChange}
                    placeholder="e.g., Acme Corporation"
                    error={formErrors.company_name}
                  />
                  {formErrors.company_name && <FormError>{formErrors.company_name}</FormError>}
                </FormGroup>

                {/* Brand Tagline */}
                <FormGroup>
                  <FormLabel>
                    <FileText size={14} />
                    Brand Tagline
                  </FormLabel>
                  <FormInput
                    type="text"
                    name="brand_tagline"
                    value={whitelabelData.brand_tagline}
                    onChange={handleInputChange}
                    placeholder="e.g., Securing Your Digital Future"
                  />
                </FormGroup>

                {/* Contact Email */}
                <FormGroup>
                  <FormLabel>
                    <Mail size={14} />
                    Contact Email
                  </FormLabel>
                  <FormInput
                    type="email"
                    name="contact_email"
                    value={whitelabelData.contact_email}
                    onChange={handleInputChange}
                    placeholder="e.g., security@acme.com"
                    error={formErrors.contact_email}
                  />
                  {formErrors.contact_email && <FormError>{formErrors.contact_email}</FormError>}
                </FormGroup>

                {/* Website */}
                <FormGroup>
                  <FormLabel>
                    <Globe size={14} />
                    Website
                  </FormLabel>
                  <FormInput
                    type="url"
                    name="website"
                    value={whitelabelData.website}
                    onChange={handleInputChange}
                    placeholder="e.g., https://acme.com"
                    error={formErrors.website}
                  />
                  {formErrors.website && <FormError>{formErrors.website}</FormError>}
                </FormGroup>

                {/* Footer Text */}
                <FormGroup>
                  <FormLabel>
                    <FileText size={14} />
                    Footer Text
                  </FormLabel>
                  <FormInput
                    type="text"
                    name="footer_text"
                    value={whitelabelData.footer_text}
                    onChange={handleInputChange}
                    placeholder="e.g., Confidential Security Report"
                  />
                </FormGroup>

                {/* Logo Upload - Full width */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <FormGroup>
                    <FormLabel>
                      <Image size={14} />
                      Company Logo
                    </FormLabel>
                    <HiddenInput
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                    <LogoUploadArea 
                      onClick={() => document.getElementById('logo-upload').click()}
                      hasLogo={!!logoPreview}
                      error={formErrors.logo}
                    >
                      {logoPreview ? (
                        <LogoPreview>
                          <LogoImage src={logoPreview} alt="Logo preview" />
                          <RemoveButton onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveLogo();
                          }}>
                            <X size={14} />
                            Remove Logo
                          </RemoveButton>
                        </LogoPreview>
                      ) : (
                        <LogoPlaceholder>
                          <Upload size={24} />
                          <div>Click to upload your company logo</div>
                          <LogoInstructions>
                            PNG, JPG, or SVG (Max. 2MB)
                          </LogoInstructions>
                        </LogoPlaceholder>
                      )}
                    </LogoUploadArea>
                    {formErrors.logo && <FormError>{formErrors.logo}</FormError>}
                  </FormGroup>
                </div>
              </FormGrid>

              <FormActions>
                <SecondaryButton onClick={() => setActiveTab('preview')}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton 
                  onClick={handleDownloadBrandedPDF}
                  disabled={isDownloading || isSubmitting}
                >
                  <Save size={16} />
                  {isDownloading ? 'Generating...' : 'Generate Branded Report'}
                </PrimaryButton>
              </FormActions>
            </FormContainer>
          )}
        </PreviewCard>
      </PreviewContainer>
    </ReportWrapper>
  );
}

export default ReportPdfPage;
